import SpinnerArea from "../../../../components/SpinnerArea";
import useService from "../../../../hooks/useService";

import tasksService from "../../../../services/tasks.service";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { FormikProps, useFormik } from "formik";
import { ColleagueFeedbackTaskCommentDto } from "../../../../models/task.model";
import * as Yup from "yup";
import styles from "../Task.module.scss";
import { useContext, useState } from "react";
import MainContext from "../../../../context/mainContext";
import { useNavigate } from "react-router";
import { SaveSuccessDialog, CancelDialog } from "../TaskDialogs";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import DocumentView from "../../../../components/DocumentView";

import { TaskTypeProps } from "./TaskTypeProps";
import moment from "moment";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const ColleagueFeedback: React.FC<TaskTypeProps> = ({ taskHeader }) => {
  const { t } = useTranslation();
  const { service } = useContext(MainContext);
  const navigate = useNavigate();

  const [details, loadingDetails] = useService(() =>
    tasksService.colleagueFeedbackDetails(taskHeader.id)
  );

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelDialogShow, cancelDialogShowSet] = useState(false);

  const [requiredData, setRequiredData] = useState<string | null>(null);

  const validationSchema = Yup.object().shape({
    comments: Yup.string().nullable().max(256),
  });

  const [uploadDocument, setUploadDocument] = useState<File | null>();

  const formik = useFormik<ColleagueFeedbackTaskCommentDto>({
    initialValues: {
      comments: details?.comments,
      taskId: taskHeader.id,
    },
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const handleSaveContinueCondition = (): boolean => {
    if (!formik.values.comments && !uploadDocument && !details?.attachment) {
      setRequiredData(t("someDataRequired"));
      return false;
    }

    setRequiredData(null);
    return true;
  };

  const sendReview = (
    values: ColleagueFeedbackTaskCommentDto,
    completeTask: boolean
  ) => {
    service(
      () =>
        tasksService.colleagueFeedback(
          { ...values, completeTask },
          uploadDocument
        ),
      {
        callback: () => {
          setSaveSuccess(true);
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<ColleagueFeedbackTaskCommentDto>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  return (
    <SpinnerArea loading={saving || loadingDetails}>
      <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
        <p>
          <Trans 
                i18nKey="colleagueFeedbackLegend"
                components={{
                  bold: <strong />,
                }}
                values={{
                  deadline: taskHeader.deadline
                  ? moment(taskHeader.deadline).format("MM/DD/yyyy")
                  : "[deadline]",
                }}
              />
        </p>
        <div className={styles.peerReviewForm}>
          <div className={styles.instructionsSection}>
            <ul>
              <li>
                <p>{t("colleagueFeedbackInstructions.0")}</p>
              </li>
              <li>
                <p>{t("colleagueFeedbackInstructions.1")}</p>
              </li>
              <li>
                <p>{t("colleagueFeedbackInstructions.2")}</p>
              </li>
            </ul>
          </div>
          <div className={styles.commentSection}>
            <FormControl fullWidth>
              <FormLabel id="description-label">
                {t("addAShortCommentAboutThePublication")}
              </FormLabel>
              <TextField
                id="comments"
                name="comments"
                placeholder={t("comment")!}
                fullWidth
                rows={3}
                multiline
                value={formik.values.comments || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.comments && formik.errors.comments}
                error={
                  formik.touched.comments && Boolean(formik.errors.comments)
                }
              />
            </FormControl>
            {requiredData && (
              <FormHelperText error>{requiredData}</FormHelperText>
            )}
          </div>
          <div className={styles.uploadComments}>
            <FormLabel>{t("uploadAVersionWithYourComments")}</FormLabel>
            <FileUploader
              title="uploadComments"
              onChange={(file: File | File[] | FileWrapper[]) => {
                setUploadDocument(file as File);
              }}
              allowedExtensions={[".doc", ".docx"]}
              isReadOnly={false}
            />
            {details && details.attachment && (
              <DocumentView document={details.attachment!} />
            )}
          </div>
        </div>

        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>

          <Button
            className={styles.save}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => sendReview(formik.values, false),
                handleSaveContinueCondition
              )
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.sendComments}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => sendReview(formik.values, true),
                handleSaveContinueCondition
              )
            }
          >
            {t("sendReview")}
          </Button>
        </div>
      </form>
      <CancelDialog
        open={cancelDialogShow}
        onAccept={() => navigate(-1)}
        onClose={() => cancelDialogShowSet(false)}
      />
      <SaveSuccessDialog
        open={saveSuccess}
        onAccept={() => {}}
        onClose={() => setSaveSuccess(false)}
        title={t("saveSuccess")}
      />
    </SpinnerArea>
  );
};

export default ColleagueFeedback;
